import React, { useRef } from "react";
import { useStrategicPlansStore } from "../../../../config/stores";
import { find, flatMap, groupBy, map, trim } from "lodash";
import AntdTable from "../../../../components/common/AntdTable";
import { Collapse, Space } from "antd";
import AppButton from "../../../../components/common/AppButton";
import { useReactToPrint } from "react-to-print";
import { AiOutlineDownload } from "react-icons/ai";

const StrategicPlanReport = () => {
  const strategicPlanStore = useStrategicPlansStore();
  const { strategicPlanDetails } = strategicPlanStore;

  const data = flatMap(strategicPlanDetails.report, (activity) => {
    return flatMap(activity.indicators, (indicator) => {
      const scoreByYear = {};

      indicator.scoresByYear.forEach((element) => {
        scoreByYear[element.year] = element.score;
      });
      return {
        id: indicator.id,
        thematic_area: activity.thematic_area,
        activitySpan: activity.indicators.length,
        activity: trim(activity.activity),
        activity_id: activity.id,
        indicator: indicator.indicator,
        target: indicator.target,
        ...scoreByYear,
        score: indicator.totalScore,
        variance: indicator.variance,
        performance: indicator.performance,
      };
    });
  });

  const getRowSpan = (rowData, idx) => {
    const prevRecord = data[idx - 1];
    const nextRecord = data[idx + 1];
    const currentRecord = rowData;

    if (nextRecord && currentRecord) {
      if (
        currentRecord?.activity_id &&
        prevRecord?.activity_id &&
        nextRecord?.activity_id &&
        currentRecord?.activity_id === prevRecord?.activity_id &&
        currentRecord?.activity_id === nextRecord?.activity_id &&
        prevRecord?.activity_id === nextRecord?.activity_id
      ) {
        return {
          rowSpan: 0,
        };
      }
    }

    if (nextRecord) {
      if (
        nextRecord?.activity_id &&
        currentRecord?.activity_id &&
        currentRecord?.activity_id === nextRecord?.activity_id
      ) {
        return {
          rowSpan: currentRecord.activitySpan,
        };
      }
    }

    if (prevRecord) {
      if (
        prevRecord?.activity_id &&
        currentRecord?.activity_id &&
        currentRecord?.activity_id === prevRecord?.activity_id
      ) {
        return {
          rowSpan: 0,
        };
      }
    }

    return {
      rowSpan: 1,
    };
  };

  const columns = [
    {
      title: "ACTIVITY",
      dataIndex: "activity",
      fixed: "left",
      width: 300,
      render: (activity, rowData) => (
        <div>
          <div>
            <span className="fw-bold">ACTIVITY: </span>
            {activity}
          </div>
        </div>
      ),
      // onCell: (rowData, idx) => getRowSpan(rowData, idx),
    },
    {
      title: "INDICATOR",
      dataIndex: "indicator",
      width: 300,
    },
    {
      title: "TARGET",
      dataIndex: "target",
      width: 100,
      align: "center",
      render: (t) => Number(t).toLocaleString(),
    },
    ...map(strategicPlanDetails.years, (year) => ({
      title: year,
      dataIndex: year,
      width: 70,
      align: "center",
      render: (val) => val || "-",
    })),
    {
      title: "TOTAL",
      dataIndex: "score",
      align: "center",
      width: 70,
      render: (t) => Number(t).toLocaleString(),
    },
    {
      title: "VARIANCE",
      dataIndex: "variance",
      align: "center",
      width: 100,
      render: (t) => Number(t).toLocaleString(),
    },
    {
      title: "PERFORMANCE",
      dataIndex: "performance",
      align: "center",
      fixed: "right",
      width: 120,
      render: (p) => `${p} %`,
    },
  ];

  const contentRef = useRef();

  const handlePrint = useReactToPrint({
    contentRef,

    documentTitle: strategicPlanDetails.title,
  });

  const onPrint = () => {
    setTimeout(() => {
      handlePrint();
    }, [10]);
  };

  const groupedByThematicArea = map(
    groupBy(data, "thematic_area"),
    (items, thematicArea) => {
      return {
        thematicArea,
        items,
      };
    }
  );

  return (
    <div>
      <Collapse
        items={groupedByThematicArea.map((thematicArea) => ({
          key: thematicArea.thematicArea,
          label: thematicArea.thematicArea,
          children: (
            <>
              <AntdTable
                // title={() => (
                //   <Space>
                //     <AppButton
                //       iconBefore={<AiOutlineDownload />}
                //       onClick={onPrint}
                //       text="DOWNLOAD"
                //     />
                //   </Space>
                // )}
                pageSize={100}
                data={thematicArea.items}
                columns={columns}
              />
            </>
          ),
        }))}
      />
      <div className="d-none">
        <style type="text/css" media="print">
          {"@page { size: A4 landscape; padding: 15px; }"}
        </style>
        <div className="p-2 border m-2" ref={contentRef}>
          <h5 className="text-center fw-bold">AMANI INITIATIVE</h5>
          <p className="text-center fw-bold mb-0">
            {strategicPlanDetails.title}
          </p>
          <p>{strategicPlanDetails.description}</p>
          {/* <PrintAppraisal /> */}
          <table className="table table-bordered table-striped">
            <thead>
              <tr>
                {map(columns, (column) => (
                  <th key={column.title}>{column.title}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {map(strategicPlanDetails.report, (activity) => {
                return map(activity.indicators, (indicator) => {
                  return (
                    <tr key={indicator.id}>
                      <td>
                        <div>
                          <span className="fw-bold">ACTIVITY: </span>
                          {activity.activity}
                        </div>
                        <div>
                          <span className="fw-bold">THEMATIC AREA: </span>
                          {activity?.thematic_area}
                        </div>
                      </td>
                      <td>{indicator.indicator}</td>
                      <td>{Number(indicator.target).toLocaleString()}</td>
                      {map(strategicPlanDetails.years, (year) => {
                        const yearData =
                          find(indicator.scoresByYear, { year }) || {};

                        return (
                          <td key={year}>
                            {yearData?.score
                              ? Number(yearData?.score).toLocaleString()
                              : "-"}
                          </td>
                        );
                      })}
                      <td>{indicator.totalScore}</td>
                      <td>{Number(indicator.variance).toLocaleString()}</td>
                      <td>{indicator.performance} %</td>
                    </tr>
                  );
                });
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StrategicPlanReport;
